import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Table } from 'antd';
import Breadcrumb from '../../components/BreadCrumb/BreadCrumb';
import ReportPageIcon from '../../assets/icons/reports-active.svg';
import CustomSelectWithFilter from '../../components/Common/CustomSelect/CustomSelectWithFilter';
import axiosInstance from '../../utils/axiosInstance';
import openNotification from '../../components/Common/OpenNotification';
import Loader from '../../components/Common/LoadingSpinner/LoadingSpinner';
import '../../components/Common/CustomSelect/CustomSelect.module.scss';

const { Option } = Select;

const ProjectLevelReportPage = () => {
        const [projects, setProjects] = useState([]);
        const [projectOptions, setProjectOptions] = useState([]);
        const [selectedProject, setSelectedProject] = useState(undefined);
        const [reportType, setReportType] = useState('revenue');
        const [loading, setLoading] = useState(false);
        const [loadingProjects, setLoadingProjects] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const searchTimeoutRef = useRef(null);
        const defaultProjectSet = useRef(false);
        const [packageUnitData, setPackageUnitData] = useState([]);
        const [packageUnitLabels, setPackageUnitLabels] = useState([]);
        const [tableLoading1, setTableLoading1] = useState(false);
        const [unitPackageData, setUnitPackageData] = useState([]);
        const [unitPackageLabels, setUnitPackageLabels] = useState([]);
        const [tableLoading2, setTableLoading2] = useState(false);
        const [selectedProject1, setSelectedProject1] = useState(undefined);
        const [reportType1, setReportType1] = useState('revenue');
        // Report type options
        const reportTypeOptions = [
                { value: 'revenue', label: 'Revenue' },
                { value: 'project_cost', label: 'Project Cost' },
                { value: 'net_billing', label: 'Net Billing' },
                { value: 'cash_inflow', label: 'Cash Inflow' },
        ];

        // Fetch projects with search
        const fetchProjects = useCallback(async (search = '') => {
                setLoadingProjects(true);
                try {
                        const response = await axiosInstance.get('/api/admin/active-project', {
                                params: {
                                        limit: 100000,
                                        page: 1,
                                        search: search,
                                },
                        });

                        if (response.data?.status === 'success') {
                                const projectsData = response.data.data?.data || [];
                                setProjects(projectsData);

                                // Transform to dropdown options
                                const options = projectsData.map((project) => ({
                                        value: `${project.IO_NO}|${project.REV_NO}`, // Combined value
                                        label: `${project.PROJ_NAME}-${project.IO_NO}-${project.REV_NO}`,
                                }));
                                setProjectOptions(options);
                        } else {
                                openNotification('error', 'Error', response.data?.message || 'Failed to fetch projects');
                        }
                } catch (error) {
                        console.error('Error fetching projects:', error);
                        openNotification('error', 'Error', error?.response?.data?.message || 'Failed to fetch projects');
                } finally {
                        setLoadingProjects(false);
                }
        }, []);

        // Fetch projects on component mount
        useEffect(() => {
                fetchProjects();
        }, [fetchProjects]);

        // Set default project selection when options are loaded (only once)
        useEffect(() => {
                if (projectOptions.length > 0 && !defaultProjectSet.current && !selectedProject) {
                        const firstProjectValue = projectOptions[0].value;
                        setSelectedProject(firstProjectValue);
                        setSelectedProject1(firstProjectValue);
                        defaultProjectSet.current = true;
                }
        }, [projectOptions, selectedProject]);

        // Handle search with debounce (400ms)
        const handleSearch = useCallback(
                (searchValue) => {
                        setSearchTerm(searchValue);
                        if (searchTimeoutRef.current) {
                                clearTimeout(searchTimeoutRef.current);
                        }
                        searchTimeoutRef.current = setTimeout(() => {
                                fetchProjects(searchValue || '');
                        }, 400);
                },
                [fetchProjects],
        );

        // Transform API response data for Package Unit Wise table format with rowSpan for NATURE
        const transformPackageUnitData = useCallback((items) => {
                const tableData = [];
                let globalIndex = 0;
                items.forEach((item, itemIndex) => {
                        const nature = item.NATURE;
                        const units = item.units || [];

                        units.forEach((unit, unitIndex) => {
                                const row = {
                                        key: `${itemIndex}-${nature}-${unitIndex}-${globalIndex}`,
                                        NATURE: nature,
                                        MNF_UNIT: unit.MNF_UNIT || '--',
                                        UNIT_NAME: unit.UNIT_NAME || '--',
                                        ...unit.values.reduce((acc, value, index) => {
                                                acc[`label_${index}`] = value || 0;
                                                return acc;
                                        }, {}),
                                };
                                tableData.push(row);
                                globalIndex++;
                        });
                });
                setPackageUnitData(tableData);
        }, []);

        // Fetch package unit wise data
        const fetchPackageUnitWiseData = useCallback(async () => {
                if (!reportType || !selectedProject) {
                        setPackageUnitData([]);
                        setPackageUnitLabels([]);
                        return;
                }

                // Parse IO_NO and REV_NO from selectedProject (format: "IO_NO|REV_NO")
                const [ioNo, revNo] = selectedProject.split('|');
                if (!ioNo || !revNo) {
                        setPackageUnitData([]);
                        setPackageUnitLabels([]);
                        return;
                }

                setTableLoading1(true);
                try {
                        const response = await axiosInstance.get('/api/admin/project-level-package-unit-wise', {
                                params: {
                                        IO_NO: ioNo,
                                        REV_NO: revNo,
                                        report_type: reportType,
                                },
                        });

                        if (response.data?.status === 'success') {
                                setPackageUnitLabels(response.data.data?.labels || []);
                                transformPackageUnitData(response.data.data?.items || []);
                        } else {
                                openNotification('error', 'Error', response.data?.message || 'Failed to fetch data');
                                setPackageUnitData([]);
                                setPackageUnitLabels([]);
                        }
                } catch (error) {
                        console.error('Error fetching package unit wise data:', error);
                        openNotification('error', 'Error', error?.response?.data?.message || 'Failed to fetch data');
                        setPackageUnitData([]);
                        setPackageUnitLabels([]);
                } finally {
                        setTableLoading1(false);
                }
        }, [reportType1, selectedProject1, transformPackageUnitData]);

        // Generate columns for Package Unit Wise table
        const generatePackageUnitColumns = (labels) => {
                const columns = [
                        {
                                title: 'NATURE',
                                dataIndex: 'NATURE',
                                key: 'NATURE',
                                width: 150,
                                fixed: 'left',
                                align: 'center',
                                onCell: (record, rowIndex) => {
                                        let rowSpan = 1;
                                        const currentNature = record.NATURE;

                                        // Count consecutive rows with same NATURE
                                        for (let i = rowIndex + 1; i < packageUnitData.length; i++) {
                                                if (packageUnitData[i].NATURE === currentNature) {
                                                        rowSpan++;
                                                } else {
                                                        break;
                                                }
                                        }

                                        // If previous row has same NATURE, hide this cell
                                        if (rowIndex > 0 && packageUnitData[rowIndex - 1].NATURE === currentNature) {
                                                return { rowSpan: 0 };
                                        }

                                        return { rowSpan };
                                },
                        },
                        {
                                title: 'MNF_UNIT',
                                dataIndex: 'MNF_UNIT',
                                key: 'MNF_UNIT',
                                width: 150,
                                fixed: 'left',
                                align: 'center',
                        },
                        {
                                title: 'UNIT_NAME',
                                dataIndex: 'UNIT_NAME',
                                key: 'UNIT_NAME',
                                width: 250,
                                fixed: 'left',
                                align: 'center',
                        },
                ];

                // Add dynamic columns for each label
                labels.forEach((label, index) => {
                        columns.push({
                                title: label,
                                dataIndex: `label_${index}`,
                                key: `label_${index}`,
                                width: 120,
                                align: 'center',
                                render: (value) => {
                                        if (value === null || value === undefined) return '--';
                                        return typeof value === 'number' ? value.toLocaleString() : value;
                                },
                        });
                });

                return columns;
        };

        const handleProjectChange = (value) => {
                setSelectedProject(value);
        };
        
        const handleReportTypeChange = (value) => {
                setReportType(value);
        };
        const handleProjectChange1 = (value) => {
                setSelectedProject1(value);
        };

        const handleReportTypeChange1 = (value) => {
                setReportType1(value);
        };

        // Transform API response data for Unit Package Wise table format with rowSpan for MNF_UNIT and UNIT_NAME
        const transformUnitPackageData = useCallback((items) => {
                const tableData = [];
                let globalIndex = 0;
                items.forEach((item, itemIndex) => {
                        const mnfUnit = item.MNF_UNIT || '--';
                        const unitName = item.UNIT_NAME || '--';
                        const packages = item.packages || [];

                        packages.forEach((pkg, packageIndex) => {
                                const row = {
                                        key: `${itemIndex}-${mnfUnit}-${unitName}-${packageIndex}-${globalIndex}`,
                                        MNF_UNIT: mnfUnit,
                                        UNIT_NAME: unitName,
                                        NATURE: pkg.NATURE || '--',
                                        ...pkg.values.reduce((acc, value, index) => {
                                                acc[`label_${index}`] = value || 0;
                                                return acc;
                                        }, {}),
                                };
                                tableData.push(row);
                                globalIndex++;
                        });
                });
                setUnitPackageData(tableData);
        }, []);

        // Fetch unit package wise data
        const fetchUnitPackageWiseData = useCallback(async () => {
                if (!reportType || !selectedProject) {
                        setUnitPackageData([]);
                        setUnitPackageLabels([]);
                        return;
                }

                // Parse IO_NO and REV_NO from selectedProject (format: "IO_NO|REV_NO")
                const [ioNo, revNo] = selectedProject.split('|');
                if (!ioNo || !revNo) {
                        setUnitPackageData([]);
                        setUnitPackageLabels([]);
                        return;
                }

                setTableLoading2(true);
                try {
                        const response = await axiosInstance.get('/api/admin/project-level-unit-package-wise', {
                                params: {
                                        IO_NO: ioNo,
                                        REV_NO: revNo,
                                        report_type: reportType,
                                },
                        });

                        if (response.data?.status === 'success') {
                                setUnitPackageLabels(response.data.data?.labels || []);
                                transformUnitPackageData(response.data.data?.items || []);
                        } else {
                                openNotification('error', 'Error', response.data?.message || 'Failed to fetch data');
                                setUnitPackageData([]);
                                setUnitPackageLabels([]);
                        }
                } catch (error) {
                        console.error('Error fetching unit package wise data:', error);
                        openNotification('error', 'Error', error?.response?.data?.message || 'Failed to fetch data');
                        setUnitPackageData([]);
                        setUnitPackageLabels([]);
                } finally {
                        setTableLoading2(false);
                }
        }, [reportType, selectedProject, transformUnitPackageData]);

        // Generate columns for Unit Package Wise table
        const generateUnitPackageColumns = (labels) => {
                const columns = [
                        {
                                title: 'MNF_UNIT',
                                dataIndex: 'MNF_UNIT',
                                key: 'MNF_UNIT',
                                width: 150,
                                fixed: 'left',
                                align: 'center',
                                onCell: (record, rowIndex) => {
                                        let rowSpan = 1;
                                        const currentUnit = `${record.MNF_UNIT}-${record.UNIT_NAME}`;

                                        // Count consecutive rows with same unit
                                        for (let i = rowIndex + 1; i < unitPackageData.length; i++) {
                                                const nextUnit = `${unitPackageData[i].MNF_UNIT}-${unitPackageData[i].UNIT_NAME}`;
                                                if (nextUnit === currentUnit) {
                                                        rowSpan++;
                                                } else {
                                                        break;
                                                }
                                        }

                                        // If previous row has same unit, hide this cell
                                        if (rowIndex > 0) {
                                                const prevUnit = `${unitPackageData[rowIndex - 1].MNF_UNIT}-${unitPackageData[rowIndex - 1].UNIT_NAME}`;
                                                if (prevUnit === currentUnit) {
                                                        return { rowSpan: 0 };
                                                }
                                        }

                                        return { rowSpan };
                                },
                        },
                        {
                                title: 'UNIT_NAME',
                                dataIndex: 'UNIT_NAME',
                                key: 'UNIT_NAME',
                                width: 250,
                                fixed: 'left',
                                align: 'center',
                                onCell: (record, rowIndex) => {
                                        let rowSpan = 1;
                                        const currentUnit = `${record.MNF_UNIT}-${record.UNIT_NAME}`;

                                        for (let i = rowIndex + 1; i < unitPackageData.length; i++) {
                                                const nextUnit = `${unitPackageData[i].MNF_UNIT}-${unitPackageData[i].UNIT_NAME}`;
                                                if (nextUnit === currentUnit) {
                                                        rowSpan++;
                                                } else {
                                                        break;
                                                }
                                        }

                                        if (rowIndex > 0) {
                                                const prevUnit = `${unitPackageData[rowIndex - 1].MNF_UNIT}-${unitPackageData[rowIndex - 1].UNIT_NAME}`;
                                                if (prevUnit === currentUnit) {
                                                        return { rowSpan: 0 };
                                                }
                                        }

                                        return { rowSpan };
                                },
                        },
                        {
                                title: 'NATURE',
                                dataIndex: 'NATURE',
                                key: 'NATURE',
                                width: 150,
                                fixed: 'left',
                                align: 'center',
                        },
                ];

                // Add dynamic columns for each label
                labels.forEach((label, index) => {
                        columns.push({
                                title: label,
                                dataIndex: `label_${index}`,
                                key: `label_${index}`,
                                width: 120,
                                align: 'center',
                                render: (value) => {
                                        if (value === null || value === undefined) return '--';
                                        return typeof value === 'number' ? value.toLocaleString() : value;
                                },
                        });
                });

                return columns;
        };

        // Fetch table data when filters change
        useEffect(() => {
                fetchPackageUnitWiseData();
                // fetchUnitPackageWiseData();
        }, [fetchPackageUnitWiseData]);
        useEffect(() => {
                // fetchPackageUnitWiseData();
                fetchUnitPackageWiseData();
        }, [fetchUnitPackageWiseData]);

        return (
                <div className="pb-14">
                        <div className="px-5 flex flex-col gap-6 pt-6 pb-[34px]">
                                <Breadcrumb />
                                <div className="flex justify-between items-center">
                                        {/* Header Section - Left */}
                                        <div className="flex gap-3">
                                                <img
                                                        src={ReportPageIcon}
                                                        alt="reports"
                                                />
                                                <div>
                                                        <div className="font-inter font-semibold text-[24px] leading-[32px] tracking-[0%] text-[#101828]">Project Level Report</div>
                                                        <div className="font-inter font-normal text-[14px] leading-[24px] tracking-[0%] text-[#475467]">View and analyze project-level reports</div>
                                                </div>
                                        </div>

                                        {/* Filter Section - Right */}
                                        <div className="flex gap-[8px] items-center">
                                                <style>
                                                        {`
                                                                .report-type-select .ant-select-arrow {
                                                                        top: 50% !important;
                                                                        transform: translateY(-50%) !important;
                                                                        padding-top: 0 !important;
                                                                        margin-top: 0 !important;
                                                                }
                                                                .report-type-select .ant-select-selector {
                                                                        height: 50px !important;
                                                                        display: flex !important;
                                                                        align-items: center !important;
                                                                }
                                                        `}
                                                </style>
                                                <div className="report-type-select">
                                                        <CustomSelectWithFilter
                                                                data={reportTypeOptions}
                                                                allowClear={false}
                                                                placeholder="Select Report Type"
                                                                value={reportType}
                                                                onChange={handleReportTypeChange}
                                                                className="!h-[50px] w-[200px]"
                                                        />
                                                </div>
                                                <div className="project-select-wrapper">
                                                        <style>
                                                                {`
                                                                        .project-select-wrapper .ant-select-selector {
                                                                                height: 50px !important;
                                                                                display: flex !important;
                                                                                align-items: center !important;
                                                                        }
                                                                        .project-select-wrapper .ant-select-arrow {
                                                                                top: 50% !important;
                                                                                transform: translateY(-50%) !important;
                                                                                padding-top: 0 !important;
                                                                                margin-top: 0 !important;
                                                                        }
                                                                `}
                                                        </style>
                                                        <Select
                                                                showSearch={true}
                                                                placeholder="Select Project"
                                                                allowClear={false}
                                                                value={selectedProject}
                                                                onChange={handleProjectChange}
                                                                onSearch={handleSearch}
                                                                className="w-[350px]"
                                                                style={{ height: '50px' }}
                                                                filterOption={false}
                                                                loading={loadingProjects}>
                                                                {projectOptions.map((item) => (
                                                                        <Option
                                                                                key={item.value}
                                                                                value={item.value}>
                                                                                {item.label}
                                                                        </Option>
                                                                ))}
                                                        </Select>
                                                </div>
                                        </div>
                                </div>
                              
                                        <>
                                                {/* Project Level Package Unit Wise Report Table */}
                                                
                                                                <div className="mt-6">
                                                        <div className="font-inter font-semibold text-[18px] leading-[24px] text-[#101828] mb-4">Project Level Unit Package Wise Report</div>
                                                        { unitPackageData.length === 0 && reportType1 && selectedProject1 ? (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <div className="text-center text-gray-500 py-8">No data available</div>
                                                                </div>
                                                        ) : !reportType1 || !selectedProject1 ? (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <div className="text-center text-gray-500 py-8">Please select Report Type and Project to view data</div>
                                                                </div>
                                                        ) : (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <Table
                                                                                columns={generateUnitPackageColumns(unitPackageLabels)}
                                                                                dataSource={unitPackageData}
                                                                                pagination={false}
                                                                                loading={tableLoading2}
                                                                                bordered
                                                                                className="custom-ant-table"
                                                                                scroll={{ x: 'max-content', y: 400 }}
                                                                                rowClassName={(record, index) => 'table-row-light'}
                                                                        />
                                                                </div>
                                                        )}
                                                </div>
                                                {/* Project Level Unit Package Wise Report Table */}
                                                  <div className="flex gap-[8px] items-center justify-end">
                                                <style>
                                                        {`
                                                                .report-type-select .ant-select-arrow {
                                                                        top: 50% !important;
                                                                        transform: translateY(-50%) !important;
                                                                        padding-top: 0 !important;
                                                                        margin-top: 0 !important;
                                                                }
                                                                .report-type-select .ant-select-selector {
                                                                        height: 50px !important;
                                                                        display: flex !important;
                                                                        align-items: center !important;
                                                                }
                                                        `}
                                                </style>
                                                <div className="report-type-select">
                                                        <CustomSelectWithFilter
                                                                data={reportTypeOptions}
                                                                allowClear={false}
                                                                placeholder="Select Report Type"
                                                                value={reportType1}
                                                                onChange={handleReportTypeChange1}
                                                                className="!h-[50px] w-[200px]"
                                                        />
                                                </div>
                                                <div className="project-select-wrapper">
                                                        <style>
                                                                {`
                                                                        .project-select-wrapper .ant-select-selector {
                                                                                height: 50px !important;
                                                                                display: flex !important;
                                                                                align-items: center !important;
                                                                        }
                                                                        .project-select-wrapper .ant-select-arrow {
                                                                                top: 50% !important;
                                                                                transform: translateY(-50%) !important;
                                                                                padding-top: 0 !important;
                                                                                margin-top: 0 !important;
                                                                        }
                                                                `}
                                                        </style>
                                                        <Select
                                                                showSearch={true}
                                                                placeholder="Select Project"
                                                                allowClear={false}
                                                                value={selectedProject1}
                                                                onChange={handleProjectChange1}
                                                                onSearch={handleSearch}
                                                                className="w-[350px]"
                                                                style={{ height: '50px' }}
                                                                filterOption={false}
                                                                loading={loadingProjects}>
                                                                {projectOptions.map((item) => (
                                                                        <Option
                                                                                key={item.value}
                                                                                value={item.value}>
                                                                                {item.label}
                                                                        </Option>
                                                                ))}
                                                        </Select>
                                                </div>
                                        </div>
                                        <div className="mt-6">
                                                        <div className="font-inter font-semibold text-[18px] leading-[24px] text-[#101828] mb-4">Project Level Package Unit Wise Report</div>
                                                        {packageUnitData.length === 0 && reportType && selectedProject ? (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <div className="text-center text-gray-500 py-8">No data available</div>
                                                                </div>
                                                        ) : !reportType || !selectedProject ? (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <div className="text-center text-gray-500 py-8">Please select Report Type and Project to view data</div>
                                                                </div>
                                                        ) : (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                        <Table
                                                                                columns={generatePackageUnitColumns(packageUnitLabels)}
                                                                                dataSource={packageUnitData}
                                                                                pagination={false}
                                                                                loading={tableLoading1}
                                                                                bordered
                                                                                className="custom-ant-table"
                                                                                scroll={{ x: 'max-content', y: 400 }}
                                                                                rowClassName={(record, index) => 'table-row-light'}
                                                                        />
                                                                </div>
                                                        )}
                                                </div>
                                                
                                        </>
                                
                        </div>
                </div>
        );
};

export default ProjectLevelReportPage;
